/**
 * Firestore schema for Agent (Business Partner) tools.
 * All collections are under the agents namespace, separate from main site data.
 *
 * Collections:
 * - agentAllowlist: emails allowed to access /agents (strict match)
 * - agentUsers: profile + metadata for each logged-in agent (synced on login)
 * - agentToolUsage: raw usage events (tool_open, step_completed, report_generated, report_shared, etc.)
 * - agentReports: per-tool report storage (when each tool implements saving)
 */

export const COLLECTIONS = {
  ALLOWLIST: 'agentAllowlist',
  USERS: 'agentUsers',
  TOOL_USAGE: 'agentToolUsage',
  REPORTS: 'agentReports',
  DRAFTS: 'agentDrafts',
  // FNA-specific collections (client-centric, email as unique key per agent)
  FNA_CLIENTS: 'fnaClients',
  FNA_FORM_DATA: 'fnaFormData',
  FNA_REPORTS: 'fnaReports',
};

/**
 * Create Firestore document ID for FNA client/form/report.
 * Format: {userId}_{sanitizedEmail} - unique per agent per client.
 * Client email is the unique property in the DB.
 */
export function fnaClientDocId(userId, clientEmail) {
  const email = (clientEmail || '').toLowerCase().trim();
  if (!email || !userId) return null;
  const safe = email.replace(/@/g, '_at_').replace(/\./g, '_');
  return `${userId}_${safe}`;
}

/**
 * agentAllowlist document id = normalized email (lowercase).
 * Fields: email (string), addedAt (timestamp), addedBy (string, optional)
 */
export function allowlistDocId(email) {
  return (email || '').toLowerCase().trim();
}

/**
 * agentUsers document id = Firebase Auth uid.
 * Fields: uid, email, displayName, photoURL, role?, agency?, createdAt, updatedAt
 */
export function agentUserDocId(uid) {
  return uid;
}

/**
 * agentToolUsage: each document = one event.
 * Fields: userId, userEmail, toolId, eventType, payload?, createdAt
 * eventType: tool_open | step_completed | report_generated | report_shared | export_pdf | time_spent
 */
export const USAGE_EVENT_TYPES = {
  TOOL_OPEN: 'tool_open',
  STEP_COMPLETED: 'step_completed',
  REPORT_GENERATED: 'report_generated',
  REPORT_SHARED: 'report_shared',
  EXPORT_PDF: 'export_pdf',
  TIME_SPENT: 'time_spent',
};

/**
 * Tool IDs for usage tracking and routing.
 */
export const TOOL_IDS = {
  FINANCIAL_NEED_ANALYSIS: 'financial-need-analysis',
  INVESTMENT_COMPARISON: 'investment-comparison',
  IMO_COMPENSATION: 'imo-compensation',
  PLACEHOLDER: 'placeholder',
};
